import { useCallback, Children, cloneElement, ReactElement, ReactNode, MouseEvent, KeyboardEvent } from 'react';

import { useSnapshot } from '@pukka/valtio';

import { useSelectModalStateContext } from '../SelectModal.state';

import { useSelectModalItemState, createSelectModalItemContext } from './SelectModalItem.state';

interface InjectedProps<T, S, D, H, Event> {
  readonly onClick: (...event: Event[]) => void;
  readonly selected: S;
  readonly disabled: D;
  readonly updating: boolean;
  readonly hidden: H;
  readonly item: T;
}

interface SelectItemCommonProps<T, S, D, H> {
  readonly item: T;
  readonly isSelected?: (item: T, selectedItems: Set<T>) => S;
  readonly isDisabled?: (item: T, disabledItems: Set<T>, isAllSelected: boolean, isSelected: S) => D;
  readonly isHidden?: (item: T, filteredItems: Set<T>) => H;
}

interface SelectItemFunctionProps<T, S, D, H, Event> {
  readonly asChild?: never;
  readonly children: ((props: InjectedProps<T, S, D, H, Event>) => JSX.Element) | ReactNode;
}

interface SelectItemAsChildProps<T, S, D, H, Event> {
  readonly asChild: true;
  readonly children: ReactElement<Partial<InjectedProps<T, S, D, H, Event>>>;
}

type SelectItemProps<T, S, D, H, Event> = SelectItemCommonProps<T, S, D, H> &
  (SelectItemFunctionProps<T, S, D, H, Event> | SelectItemAsChildProps<T, S, D, H, Event>);

function defaultIsSelectedFn<T>(item: T, selectedItems: Set<T>) {
  return selectedItems.has(item);
}

function defaultIsDisabledFn<T>(item: T, disabledItems: Set<T>, isAllSelected: boolean, isSelected: boolean) {
  return (
    disabledItems.has(item) ||
    // This happens when max item selected is reached
    (isAllSelected && !isSelected)
  );
}

function defaultIsHiddenFn<T>(item: T, filteredItems: Set<T>) {
  return !filteredItems.has(item);
}

function SelectModalItemBaseFactory<T, S, D, H>(
  defaultIsSelected: (item: T, selectedItems: Set<T>) => S,
  defaultIsDisabled: (item: T, disabledItems: Set<T>, isAllSelected: boolean, isSelected: S) => D,
  defaultIsHidden: (item: T, filteredItems: Set<T>) => H,
  defaultState: {
    selected: S;
    disabled: D;
    hidden: H;
    updating: boolean;
  },
) {
  const ItemContext = createSelectModalItemContext<S, D, H>();

  function SelectModalItemBase<TT extends T, E extends MouseEvent>({
    item,
    asChild,
    isSelected = defaultIsSelected,
    isDisabled = defaultIsDisabled,
    isHidden = defaultIsHidden,
    children,
  }: SelectItemProps<TT, S, D, H, E>) {
    const itemState = useSelectModalItemState(defaultState);
    const state = useSelectModalStateContext<TT>();
    const snapped = useSnapshot(state);
    const { onToggle } = state;
    const selected = isSelected(item, snapped.selected);
    const disabled = isDisabled(item, snapped.disabled, snapped.isAllSelected, selected);
    const hidden = isHidden(item, snapped.filter.items);

    // Re-setting the same value won't trigger subscription or changes
    itemState.selected = selected;
    itemState.disabled = disabled;
    itemState.hidden = hidden;
    itemState.updating = snapped.filter.isUpdating;

    const onClick = useCallback(() => {
      if (!disabled && !snapped.filter.isUpdating) {
        onToggle(item);
      }
    }, [onToggle, item, disabled, snapped.filter.isUpdating]);

    const onKeyUpHandler = useCallback(
      (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
          onClick();
        }
      },
      [onClick],
    );

    if (asChild) {
      return (
        <ItemContext.Provider value={itemState}>
          {Children.map(children, (child) => {
            const originalOnClick = child.props.onClick;

            return cloneElement(child, {
              onClick(...args: E[]) {
                originalOnClick?.(...args);
                onClick();
              },
              ...itemState,
              item,
            });
          })}
        </ItemContext.Provider>
      );
    }

    if (typeof children === 'function') {
      return (
        <ItemContext.Provider value={itemState}>
          {children({
            onClick,
            ...itemState,
            item,
          })}
        </ItemContext.Provider>
      );
    }

    return (
      <ItemContext.Provider value={itemState}>
        <div
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyUp={onKeyUpHandler}
          data-select-modal-item-selected={itemState.selected}
          data-select-modal-item-disabled={itemState.disabled}
          data-select-modal-item-hidden={itemState.hidden}
          data-select-modal-item-updating={itemState.updating}
        >
          {children}
        </div>
      </ItemContext.Provider>
    );
  }

  SelectModalItemBase.Factory = SelectModalItemBaseFactory;

  return {
    Component: SelectModalItemBase,
    Context: ItemContext,
  };
}

const { Component: SelectModalItemBase, Context } = SelectModalItemBaseFactory(
  defaultIsSelectedFn,
  defaultIsDisabledFn,
  defaultIsHiddenFn,
  {
    disabled: false,
    selected: false,
    hidden: false,
    updating: false,
  },
);

export { SelectModalItemBase, Context };
